import { RefObject } from "react"
import { action, computed, makeObservable, observable, override } from "mobx"
import { ProgressProps, Upload, UploadFile } from "antd"
import { RcFile, UploadChangeParam } from "antd/es/upload"
import { UploadRef } from "antd/es/upload/Upload"

import { client } from "@src/shared/api"
import { loading } from "@src/shared/classes/utils/loading"
import {
  defaultUploadedState,
  IUploadedState,
  Uploaded,
} from "@src/shared/componemts/ImageUploadUI/entity/Uploaded"

export interface IImageUploadState extends IUploadedState {
  cacheId: null | string
  isRemoved: boolean
  preview: null | string
  uploadUrl?: string
}

export class ImageUpload extends Uploaded {
  _state: IImageUploadState = {
    cacheId: null,
    isRemoved: false,
    preview: null,
    ...super._state,
  }

  constructor(data: Pick<IImageUploadState, "uploadUrl"> = {}) {
    super()

    makeObservable(this, {
      _state: override,
      blob: computed,
      cacheId: computed,
      isRemoved: computed,
      onChange: override,
      preview: computed,
      upload: action.bound,
    })

    this.setState(data)
  }

  static toBase64(file: UploadFile<any>): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.readAsDataURL(file as RcFile)

      reader.onload = () => resolve(reader.result as string)

      reader.onerror = (error) => reject(error)
    })
  }

  async _upload(file: Blob) {
    if (this._state.uploadUrl) {
      const form = new FormData()

      form.append("file", file)

      const {
        data: {
          data: { cache_id },
        },
      } = await client.post(this._state.uploadUrl, form)

      this.setState({
        ...this._state,
        cacheId: cache_id,
        isRemoved: false,
      })
    }
  }

  async onChange({
    file,
    fileList,
  }: UploadChangeParam<UploadFile<any>>): Promise<void> {
    if (file) {
      this.setState({
        preview: await ImageUpload.toBase64(file),
      })
    }

    return super.onChange({ file, fileList })
  }

  remove() {
    this.setState({
      isRemoved: true,
      preview: null,
      source: null,
    })
  }

  get blob() {
    const { source } = this._state

    if (source) {
      const { originFileObj } = source

      return (originFileObj as Blob) ?? null
    }

    return null
  }

  get cacheId() {
    return this._state.cacheId
  }

  get isRemoved() {
    return this._state.isRemoved
  }

  get preview() {
    return this._state.preview
  }
}
